import { styled } from 'styled-components'

import {
  isZeroQuotePlaceholder,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedCommonListingDetails,
  type OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { Banner, FormattedMessage, Modal } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import { CardOrder } from '@/buyer-portal/components/CardOrder'
import { ErrorBannerComponent } from '@/buyer-portal/components/ErrorBanner'
import { useCalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import { useSuppressShippingLine } from '@/buyer-portal/components/features/listing/hooks/useSuppressShippingLine'
import { useTotalPaymentInfo } from '@/buyer-portal/components/features/listing/hooks/useTotalPaymentInfo'
import { isSellerCollectsLot } from '@/buyer-portal/helpers/useIsSellerCollectsSale'
import activeSubscriptionsQuery from '@/buyer-portal/queries/activeSubscriptionsQuery'
import { FeatureFlags } from '@/helpers/featureFlags'

import { ActionSection } from './ActionSection'
import { InfoBanner } from './InfoBanner'

const Container = styled(Modal)`
  border-radius: 0.5rem;

  @media ${Breakpoints.min.medium} {
    max-width: min(90vw, 43.75rem);
  }
`

const BidDetailsHeader = styled.div`
  ${Typography.Title3}
  display: flex;
  justify-content: space-between;
`

const StyledModalContent = styled(Modal.Content)`
  ${Typography.Body3}

  @media ${Breakpoints.max.medium} {
    > p:first-child {
      margin-top: 0;
    }
  }
`

const BidDetailsContainer = styled.div`
  padding-top: 1.5rem;
  border-top: 0.0625rem solid ${designColors.neutral.mediumGray};
  border-bottom: 0.0625rem solid ${designColors.neutral.mediumGray};
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const FirstBid = styled.div`
  padding-bottom: 1rem;
`

const BidDetailsContent = styled.div`
  padding: 0.75rem 0;
  > ul {
    margin-left: 0;
    margin-bottom: 0;
    > li {
      padding: 0;
      .estimated-total-payment-description {
        ${Typography.Body4}
        color: ${designColors.neutral.darkGray};
      }
    }
  }
`

const StyledBanner = styled(Banner)`
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`
const CcAcceptedBanner = styled(Banner)`
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
`

const ContractListingBanner = styled(Banner)`
  margin-bottom: 1.5rem;
`

export const GenericSaleProcessConfirmationModal = ({
  listing,
  ns,
  isLoading,
  error,
  formattedAmount,
  dollarAmount,
  onConfirm,
  closeModal,
  selectedQuote,
  totalTaxCents = 0,
  vatDisplay,
}: {
  listing: OrchestratedCommonListingDetails
  ns: string
  isLoading: boolean
  error: unknown
  formattedAmount: React.ReactNode
  // note that dollar amount should be null for relative bidBasis
  dollarAmount: number | null
  onConfirm: () => void
  closeModal: () => void
  selectedQuote?: OrchestratedQuote | null
  totalTaxCents?: number
  vatDisplay?: React.ReactNode
}) => {
  const { data: sellerSubscription } = activeSubscriptionsQuery.useQuery(
    listing.accountId
  )

  const additionalCharges = useCalculatedAdditionalCharges({
    buyersPremium: sellerSubscription?.buyersPremium ?? 0,
    currentBidDollars: dollarAmount ?? 0,
    selectedQuote,
  })

  // No payment method has been selected at offer-creation time, so
  // selectedPaymentMethod / isWireSelected are intentionally omitted —
  // the convenience fee row is shown later in the checkout flow once the
  // offer is accepted and the buyer picks a payment method.
  // FP-3356 — the same suppression the bid and offer cards apply. B-Stock
  // only quotes freight it books itself, so on every other Seller Collects
  // mode a pickup quote of `totalPrice: 0` would print here as "$0.00".
  const suppressShippingLine = useSuppressShippingLine(listing, selectedQuote)

  const totalPaymentInfo = useTotalPaymentInfo({
    purchaseAmountDollars: dollarAmount ?? 0,
    selectedQuote,
    currency: listing.currency ?? 'USD',
    additionalCharges,
    shipping: listing.shipping ?? null,
    isOffer: listing.pricingStrategy === 'MAKE_AN_OFFER',
    totalTaxCents,
    suppressShippingLine,
    isSellerCollects: isSellerCollectsLot(listing),
  })

  const isContract = listing.listingType === 'CONTRACT'

  const isZeroQuoteValue = isZeroQuotePlaceholder(selectedQuote)

  const enableBalanceCreditCards = useFlag(
    FeatureFlags.ENABLE_BALANCE_CREDIT_CARDS
  )
  const showCcAcceptedBanner =
    enableBalanceCreditCards && listing.isCreditCardEnabled === true

  return (
    <Container
      closeModal={
        isLoading
          ? () => {
              //do nothing, prevent from closing modal
              return
            }
          : closeModal
      }
    >
      <Modal.Header>
        <FormattedMessage id={`${ns}.title`} />
      </Modal.Header>
      <StyledModalContent>
        {isContract && (
          <ContractListingBanner type="info">
            <FormattedMessage id="Listing.confirmationModal.banner.contractListing" />
          </ContractListingBanner>
        )}
        <FirstBid>
          <FormattedMessage
            id={`${ns}.description${isContract ? 'Contract' : ''}`}
          />
        </FirstBid>
        <BidDetailsContainer>
          <BidDetailsHeader>
            <FormattedMessage id={`${ns}.amountLabel`} />
            <span>{formattedAmount}</span>
          </BidDetailsHeader>
          <div>
            {isZeroQuoteValue && (
              <StyledBanner type="warning">
                <FormattedMessage id="Listing.confirmationModal.banner.noShippingFee" />
              </StyledBanner>
            )}
            {showCcAcceptedBanner && (
              <CcAcceptedBanner type="info">
                <FormattedMessage id="BidConfirmationModal.creditCardAcceptedBanner" />
              </CcAcceptedBanner>
            )}
            {!isContract ? (
              // Estimated payment on a contract is difficult to predict, esp
              // when bidding in percentage of COGS or MSRP. For now we simply
              // don't try.
              <BidDetailsContent>
                <CardOrder
                  data={totalPaymentInfo}
                  level={2}
                  autoCollapseOnDataChange
                />
                {vatDisplay}
              </BidDetailsContent>
            ) : null}
          </div>
        </BidDetailsContainer>
        <InfoBanner listing={listing} />
        {error ? <ErrorBannerComponent error={error} /> : null}
      </StyledModalContent>
      <ActionSection
        closeModal={closeModal}
        isLoading={isLoading}
        listing={listing}
        onConfirm={onConfirm}
        confirmButtonLabel={<FormattedMessage id={`${ns}.confirmButton`} />}
      />
    </Container>
  )
}
